/**
 * Phase 97 — Formulaire de retour post-tournée.
 * Route : /tournees-reappro/:id/execute (conservée pour compat, mais UI complètement refaite)
 *
 * Ancien workflow (Lot 2 Phase 88) : tablette + scan EAN13 sur place.
 * Nouveau workflow (Phase 97) :
 *   - Le stock est sorti de la centrale au verrouillage (lock-and-print)
 *   - Le terrain saisit les stocks directement dans MOMA (pas de tablette ERP)
 *   - Au retour au dépôt, on saisit UNIQUEMENT :
 *     * DLC ramenées (barquettes retirées des machines car expirées)
 *     * Manquants constatés (écarts non expliqués par les ventes MOMA)
 *   - Une note libre par ligne (optionnel)
 *   - Un bouton "Clôturer la tournée (RAS)" pour le cas nominal (0 DLC / 0 manquant)
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Loader2, Undo2, AlertCircle, Package } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

export default function RefillTourReturn() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tour, setTour] = useState(null);
  // Map par (distrib_id-product_id) → { dlc_expired_qty, missing_qty, notes }
  const [returnMap, setReturnMap] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/refill-tours/${id}`);
        setTour(data);
        const init = {};
        for (const ln of data.lines || []) {
          // Phase 98 — clé par (distrib, product, slot) pour distinguer multi-slot
          const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
          init[key] = {
            dlc_expired_qty: Number(ln.dlc_expired_qty || 0),
            missing_qty: Number(ln.missing_qty || 0),
            notes: ln.return_notes || "",
          };
        }
        setReturnMap(init);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur chargement");
        navigate("/tournees-reappro");
      }
    })();
  }, [id, navigate]);

  // Groupage par distributeur
  const distribGroups = useMemo(() => {
    if (!tour) return [];
    const grp = {};
    for (const ln of tour.lines || []) {
      // On ne montre que les lignes qui ont réellement quitté la centrale
      const qty = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
      if (qty <= 0) continue;
      const did = ln.distributor_id;
      if (!grp[did]) {
        grp[did] = {
          distributor_id: did,
          distributor_name: ln.distributor_name,
          distributor_code: ln.distributor_code,
          distributor_city: ln.distributor_city,
          lines: [],
        };
      }
      grp[did].lines.push(ln);
    }
    const ordered = (tour.selected_distributor_ids || []).map((did) => grp[did]).filter(Boolean);
    const orphans = Object.keys(grp).filter((did) => !(tour.selected_distributor_ids || []).includes(did));
    orphans.forEach((did) => ordered.push(grp[did]));
    ordered.forEach((g) => g.lines.sort((a, b) => {
      const sa = parseInt(String(a.slot_number || a.slot || "9999").split(/[,/]/)[0], 10);
      const sb = parseInt(String(b.slot_number || b.slot || "9999").split(/[,/]/)[0], 10);
      if (sa !== sb) return sa - sb;
      return (a.product_name || "").localeCompare(b.product_name || "");
    }));
    return ordered;
  }, [tour]);

  const updateReturn = (distributor_id, product_id, slot_number, patch) => {
    const key = `${distributor_id}::${product_id}::${slot_number || ""}`;
    setReturnMap((m) => ({ ...m, [key]: { ...m[key], ...patch } }));
  };

  const totals = useMemo(() => {
    let dlc = 0;
    let missing = 0;
    let touched = 0;
    for (const key in returnMap) {
      const r = returnMap[key];
      const d = Number(r.dlc_expired_qty || 0);
      const m = Number(r.missing_qty || 0);
      dlc += d;
      missing += m;
      if (d > 0 || m > 0 || (r.notes && r.notes.trim())) touched += 1;
    }
    return { dlc, missing, touched };
  }, [returnMap]);

  const submitReturn = async () => {
    // On garde toutes les lignes avec (dlc > 0 OU missing > 0 OU notes)
    const lines = Object.entries(returnMap)
      .filter(([_, r]) => Number(r.dlc_expired_qty || 0) > 0 || Number(r.missing_qty || 0) > 0 || (r.notes && r.notes.trim()))
      .map(([key, r]) => {
        // Phase 98 — clé "distributor_id::product_id::slot_number"
        const parts = key.split("::");
        const distributor_id = parts[0];
        const product_id = parts[1];
        const slot_number = parts[2] || "";
        return {
          distributor_id,
          product_id,
          slot_number,
          dlc_expired_qty: Number(r.dlc_expired_qty || 0),
          missing_qty: Number(r.missing_qty || 0),
          notes: r.notes || "",
        };
      });

    const isRas = lines.length === 0;
    const title = isRas ? "Clôturer la tournée (RAS) ?" : "Confirmer le retour de tournée ?";
    const description = isRas
      ? "Aucune DLC ramenée ni manquant. La tournée sera clôturée sans mouvement de stock supplémentaire."
      : `Vous allez enregistrer :\n• ${totals.dlc} barquette(s) DLC retirée(s)\n• ${totals.missing} barquette(s) manquante(s)\n\nDes mouvements WASTE seront créés sur les distributeurs concernés. La tournée passe en statut CLOTUREE.`;

    const ok = await confirm({
      title,
      description,
      confirmText: isRas ? "Clôturer (RAS)" : "Confirmer le retour",
      destructive: !isRas,
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      const { data } = await api.post(`/refill-tours/${id}/return`, { lines });
      const nbDlc = (data.dlc_movements || []).length;
      const nbMiss = (data.missing_movements || []).length;
      const nbWarn = (data.warnings || []).length;
      toast.success(
        isRas
          ? "Tournée clôturée (RAS)"
          : `Retour saisi — ${nbDlc} DLC + ${nbMiss} manquants enregistrés${nbWarn ? ` · ${nbWarn} avertissement(s)` : ""}`
      );
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      // Phase 98ae — Toast détaillé sur la sync Nayax MOMA d'ajustement
      const sync = data.nayax_return_sync;
      if (sync && sync.total_distributors > 0) {
        const { success_count = 0, failed_count = 0, skipped_count = 0, total_distributors = 0 } = sync;
        if (failed_count === 0 && success_count > 0) {
          toast.success(`📡 Stock Nayax MOMA ajusté sur ${success_count}/${total_distributors} distributeur(s)`);
        } else if (failed_count > 0) {
          toast.warning(`📡 Nayax : ${success_count} ajusté(s), ${failed_count} en échec, ${skipped_count} ignoré(s) — voir historique sync`);
        } else if (skipped_count === total_distributors) {
          toast.info(`📡 Nayax : ${skipped_count} distributeur(s) ignoré(s) (non configuré)`);
        }
      }
      navigate(`/tournees-reappro/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur clôture retour");
    } finally {
      setSubmitting(false);
    }
  };

  if (!tour) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Chargement…</div>;

  if (tour.status === "CLOTUREE") {
    return (
      <div className="p-8 text-center space-y-3" data-testid="tour-already-closed">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#1F4E3D]" />
        <p className="text-lg font-medium">Cette tournée est déjà clôturée.</p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)}>Voir le détail</Button>
      </div>
    );
  }

  if (tour.status !== "EN_LIVRAISON") {
    return (
      <div className="p-8 text-center space-y-3" data-testid="tour-not-locked">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C05C47]" />
        <p className="text-lg font-medium">La tournée doit d&apos;abord être verrouillée.</p>
        <p className="text-sm text-[#575A57]">
          Retournez au détail et cliquez sur <strong>« Verrouiller & Imprimer »</strong> pour sortir le stock de la centrale.
        </p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)} className="bg-[#1F4E3D] hover:bg-[#173D2F]">
          Retour au détail
        </Button>
      </div>
    );
  }

  if (distribGroups.length === 0) {
    return (
      <div className="p-8 text-center text-[#575A57]" data-testid="tour-return-empty">
        <p>Aucune ligne à traiter (aucun stock n&apos;a été sorti pour cette tournée).</p>
        <Button variant="outline" onClick={() => navigate(`/tournees-reappro/${id}`)} className="mt-3">Retour</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="refill-tour-return-page">
      {/* En-tête */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(`/tournees-reappro/${id}`)} data-testid="back-to-tour-detail">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au détail
        </Button>
        <div className="flex items-center gap-3">
          <Undo2 className="w-5 h-5 text-[#1F4E3D]" />
          <span className="font-mono font-bold">{tour.reference}</span>
          <Badge className="bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]">Retour de tournée</Badge>
        </div>
      </div>

      {/* Explication */}
      <Card className="border-[#EAF1F8] bg-[#F8FAFD]">
        <CardContent className="p-4 space-y-2 text-sm text-[#575A57]">
          <div className="font-bold text-[#2E5C8A]">Formulaire de retour post-tournée</div>
          <p>
            Le stock est déjà sorti de la centrale (au verrouillage). Les ventes descendent
            automatiquement depuis MOMA. Il vous reste seulement à saisir ci-dessous :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>DLC ramenées</strong> : barquettes retirées des machines car expirées</li>
            <li><strong>Manquants constatés</strong> : barquettes qui manquent sans explication (hors DLC et ventes)</li>
            <li>Une <strong>note</strong> par ligne si besoin (motif, contexte)</li>
          </ul>
          <p className="text-xs text-[#8C6B2F]">
            Si tout est OK, cliquez simplement sur « Clôturer (RAS) » en bas de page.
          </p>
        </CardContent>
      </Card>

      {/* Totaux temps réel */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex flex-wrap gap-6 text-sm" data-testid="return-totals">
          <div>
            <div className="text-xs text-[#575A57] uppercase">Distributeurs</div>
            <div className="text-2xl font-bold text-[#1A1C1A] font-mono">{distribGroups.length}</div>
          </div>
          <div>
            <div className="text-xs text-[#575A57] uppercase">DLC ramenées</div>
            <div className="text-2xl font-bold text-[#C05C47] font-mono" data-testid="total-dlc">{totals.dlc.toFixed(0)}</div>
          </div>
          <div>
            <div className="text-xs text-[#575A57] uppercase">Manquants</div>
            <div className="text-2xl font-bold text-[#8C6B2F] font-mono" data-testid="total-missing">{totals.missing.toFixed(0)}</div>
          </div>
          <div>
            <div className="text-xs text-[#575A57] uppercase">Lignes renseignées</div>
            <div className="text-2xl font-bold text-[#1F4E3D] font-mono" data-testid="total-touched">{totals.touched}</div>
          </div>
        </CardContent>
      </Card>

      {/* 1 carte par distributeur */}
      {distribGroups.map((g) => (
        <Card key={g.distributor_id} className="border-[#E5E5E0]" data-testid={`return-distrib-card-${g.distributor_id}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-[#E5E5E0] bg-[#F4F4F0]">
              <div className="font-bold text-[#1A1C1A] flex items-center gap-2 flex-wrap">
                <Package className="w-4 h-4 text-[#575A57]" />
                {g.distributor_name}
              </div>
              <div className="text-xs text-[#575A57] mt-0.5">
                {g.distributor_code && <span className="font-mono mr-2">{g.distributor_code}</span>}
                {g.distributor_city}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7] text-[#575A57] text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Produit</th>
                  <th className="text-right px-3 py-2 font-semibold">Envoyé</th>
                  <th className="text-right px-3 py-2 font-semibold w-24">DLC ramenées</th>
                  <th className="text-right px-3 py-2 font-semibold w-24">Manquants</th>
                  <th className="text-left px-3 py-2 font-semibold">Note</th>
                </tr>
              </thead>
              <tbody>
                {g.lines.map((ln) => {
                  const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
                  const r = returnMap[key] || {};
                  const shipped = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
                  const testKey = `${g.distributor_code || g.distributor_id.slice(0,6)}-${ln.slot_number || ln.product_sku || "x"}`;
                  return (
                    <tr key={key} className="border-t border-[#E5E5E0]" data-testid={`return-line-${testKey}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {ln.slot_number && (
                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-[#1F4E3D] text-white font-mono text-[10px] font-bold">
                              {ln.slot_number}
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-[#1A1C1A]">{ln.product_name}</div>
                            {ln.product_sku && <div className="text-xs text-[#A3A6A3] font-mono">{ln.product_sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#575A57]">{shipped.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={r.dlc_expired_qty ?? 0}
                          onChange={(e) => updateReturn(ln.distributor_id, ln.product_id, ln.slot_number, { dlc_expired_qty: Number(e.target.value || 0) })}
                          className="w-20 text-right font-mono h-9"
                          data-testid={`return-dlc-${testKey}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={r.missing_qty ?? 0}
                          onChange={(e) => updateReturn(ln.distributor_id, ln.product_id, ln.slot_number, { missing_qty: Number(e.target.value || 0) })}
                          className="w-20 text-right font-mono h-9"
                          data-testid={`return-missing-${testKey}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="text"
                          value={r.notes || ""}
                          onChange={(e) => updateReturn(ln.distributor_id, ln.product_id, ln.slot_number, { notes: e.target.value })}
                          placeholder="Optionnel…"
                          className="text-xs h-9"
                          data-testid={`return-notes-${testKey}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {/* Actions de clôture */}
      <Card className="border-[#1F4E3D]/40 bg-[#EAF4EE] sticky bottom-4">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-bold text-[#1A1C1A]">Tout est saisi ?</div>
            <div className="text-sm text-[#575A57]">
              {totals.touched === 0
                ? "Aucune anomalie — cliquez pour clôturer en RAS."
                : `${totals.touched} ligne(s) renseignée(s) · ${totals.dlc} DLC · ${totals.missing} manquants`}
            </div>
          </div>
          <Button
            onClick={submitReturn}
            disabled={submitting}
            size="lg"
            className={totals.touched === 0 ? "bg-[#1F4E3D] hover:bg-[#173D2F]" : "bg-[#C05C47] hover:bg-[#A04E2C]"}
            data-testid="submit-return-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            {totals.touched === 0 ? "Clôturer (RAS)" : "Confirmer le retour et clôturer"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
